import { useRef, useCallback, useEffect } from 'react';
import { FiBold, FiItalic, FiUnderline } from 'react-icons/fi';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

export default function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const internalValue = useRef(value);
  const isInitialized = useRef(false);

  // Set initial content on mount
  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      editorRef.current.innerHTML = value;
      internalValue.current = value;
      isInitialized.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync innerHTML from prop when it changes externally (not from our own input)
  useEffect(() => {
    if (editorRef.current && isInitialized.current && value !== internalValue.current) {
      editorRef.current.innerHTML = value;
      internalValue.current = value;
    }
  }, [value]);

  const emitChange = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      internalValue.current = html;
      onChange(html);
    }
  }, [onChange]);

  const execCommand = useCallback((command: string, val?: string) => {
    document.execCommand(command, false, val);
    editorRef.current?.focus();
    emitChange();
  }, [emitChange]);

  const handleFontSize = useCallback((size: string) => {
    document.execCommand('fontSize', false, size);
    editorRef.current?.focus();
    emitChange();
  }, [emitChange]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  const stopDrag = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  return (
    <div className="rich-text-editor" onDragStart={stopDrag} draggable={false}>
      <div className="rte-toolbar">
        <button
          type="button"
          className="rte-btn"
          onMouseDown={(e) => { e.preventDefault(); execCommand('bold'); }}
          title="Bold"
        >
          <FiBold />
        </button>
        <button
          type="button"
          className="rte-btn"
          onMouseDown={(e) => { e.preventDefault(); execCommand('italic'); }}
          title="Italic"
        >
          <FiItalic />
        </button>
        <button
          type="button"
          className="rte-btn"
          onMouseDown={(e) => { e.preventDefault(); execCommand('underline'); }}
          title="Underline"
        >
          <FiUnderline />
        </button>
        <div className="rte-separator" />
        <select
          className="rte-font-size-select"
          onChange={(e) => handleFontSize(e.target.value)}
          defaultValue="3"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <option value="1">Small</option>
          <option value="2">Normal-</option>
          <option value="3">Normal</option>
          <option value="4">Large</option>
          <option value="5">X-Large</option>
          <option value="6">XX-Large</option>
        </select>
      </div>
      <div
        ref={editorRef}
        className="rte-content"
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onPaste={handlePaste}
        data-placeholder={placeholder || 'Enter content...'}
      />
    </div>
  );
}
